import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, AREA_GUIDES } from "../mock";
import { useAppModals } from "./ValuationDialog";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { openValuation, openRegister } = useAppModals();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (id) => {
    if (id === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleNav = (link) => {
    setOpen(false);
    if (link.type === "modal") {
      if (link.target === "valuation") openValuation();
      if (link.target === "register") openRegister();
      return;
    }
    if (link.type === "route") {
      navigate(link.target);
      return;
    }
    // section link
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: link.target } });
    } else {
      scrollToSection(link.target);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 shadow-md backdrop-blur" : "bg-white/85 backdrop-blur"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-2 lg:px-8">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center lg:flex">
          {NAV_LINKS.map((l, i) => (
            <React.Fragment key={l.label}>
              {l.type === "dropdown" ? (
                <div
                  className="relative"
                  onMouseEnter={() => setOpenMenu(l.label)}
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  <button className="flex items-center gap-1 px-3 py-2 text-[15px] font-semibold text-[#16323f] transition-colors hover:text-[#ec008c]">
                    {l.label}
                    <ChevronDown size={15} className={`transition-transform ${openMenu === l.label ? "rotate-180" : ""}`} />
                  </button>
                  {openMenu === l.label && (
                    <div className="absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 pt-2">
                      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white py-2 shadow-xl">
                        {(l.children || AREA_GUIDES.map((a) => ({ label: a.name, to: `/areas/${a.slug}` }))).map((c) => (
                          <button
                            key={(c.to || c.target) + c.label}
                            onClick={() => { setOpenMenu(null); c.to ? navigate(c.to) : handleNav(c); }}
                            className="block w-full px-5 py-2.5 text-left text-sm font-medium text-[#16323f] transition-colors hover:bg-[#faf7f0] hover:text-[#ec008c]"
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleNav(l)}
                  className="px-3 py-2 text-[15px] font-semibold text-[#16323f] transition-colors hover:text-[#ec008c]"
                >
                  {l.label}
                </button>
              )}
              {i < NAV_LINKS.length - 1 && <span className="text-gray-300">|</span>}
            </React.Fragment>
          ))}
        </nav>

        {/* Mobile toggle */}
        <button
          className="text-[#16323f] lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="border-t border-gray-100 bg-white lg:hidden">
          <nav className="flex flex-col px-4 py-2">
            {NAV_LINKS.map((l) =>
              l.type === "dropdown" ? (
                <div key={l.label}>
                  <button
                    onClick={() => setOpenMenu((v) => (v === l.label ? null : l.label))}
                    className="flex w-full items-center justify-between border-b border-gray-100 py-3 text-left text-[15px] font-semibold text-[#16323f]"
                  >
                    {l.label}
                    <ChevronDown size={16} className={`transition-transform ${openMenu === l.label ? "rotate-180" : ""}`} />
                  </button>
                  {openMenu === l.label && (
                    <div className="border-b border-gray-100 bg-[#faf7f0] py-1">
                      {(l.children || AREA_GUIDES.map((a) => ({ label: a.name, to: `/areas/${a.slug}` }))).map((c) => (
                        <button
                          key={(c.to || c.target) + c.label}
                          onClick={() => { setOpen(false); setOpenMenu(null); c.to ? navigate(c.to) : handleNav(c); }}
                          className="block w-full py-2.5 pl-6 pr-4 text-left text-sm font-medium text-[#16323f]"
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={l.label}
                  onClick={() => handleNav(l)}
                  className="border-b border-gray-100 py-3 text-left text-[15px] font-semibold text-[#16323f]"
                >
                  {l.label}
                </button>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
